// This is the modal that allows the user to edit the RdSAP dimension details.

import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { FormBuilder, FormGroup, FormArray } from '@angular/forms';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { find } from 'lodash';

@Component({
  selector: 'app-rd-sap-dimension-details-modal',
  templateUrl: './rdsap-dimension-details-modal.component.html',
})
export class RdSAPDimensionDetailsModalComponent implements OnInit {
  @Input() numberOfFloors: number;
  @Input() buildingDimensionDetails: any[] = [];
  @Input() hasActiveDataSet: boolean;
  @Input() activeDataSetNumberOfFloors = 0;
  @Input() activeDataSetBuildingDimensionDetails: any[] = [];
  @Input() isEdit: boolean;
  @Input() modifiedBackgroundClass: string;
  @Input() partyWallConstructionNotApplicable = true;
  @Input() heatedBasement: boolean;

  area = {};
  perimeter = {};
  height = {};
  partyWall = {};

  heatedBasementDetails = {
    area: null,
    height: null,
    perimeter: null,
    partyWall: null,
  };

  showErrors: boolean = false;

  @Output() closeEvent = new EventEmitter<any>();

  buildingDimensionsDetails: FormGroup;
  dimensionForm: FormGroup;
  numberOfFloorsOptions = this.generateRange(1, 7);

  constructor(
    private fb: FormBuilder,
    public activeModal: NgbActiveModal,
  ) { }

  ngOnInit(): void {
    this.setStates();
    this.createForm();
    this.setFormArray();
    this.adjustBuildingDimensions();
    this.showErrors = true;
  }

  createForm() {
    this.dimensionForm = this.fb.group({
      dimensions: this.fb.array([]),
      numberOfFloors: this.numberOfFloors,
    });
  }

  setFormArray() {
    const dimensionsArray = this.dimensionForm.get('dimensions') as FormArray;
    dimensionsArray.clear(); // Clear the form array before setting values
    this.buildingDimensionDetails.forEach((dimension) => {
      dimensionsArray.push(this.fb.group({
        floor: dimension.floor,
        area: dimension.area,
        height: dimension.height,
        perimeter: dimension.perimeter,
        partyWall: dimension.partyWall,
        heightstring: dimension.height,
        areastring: dimension.area,
        perimeterstring: dimension.perimeter,
      }));
    });
  }

  private setStates() {
    this.buildingDimensionDetails.forEach((item, index) => {
      item.floor = index.toString();
    });
  }

  adjustBuildingDimensions() {
    const newNumFloors = this.numberOfFloors;
    while (newNumFloors > this.buildingDimensionDetails.length) {
      this.buildingDimensionDetails.push({
        floor: `${this.buildingDimensionDetails.length}`,
        area: null,
        height: null,
        perimeter: null,
        partyWall: this.partyWallConstructionNotApplicable ? 0 : null
      });
    }

    while (newNumFloors < this.buildingDimensionDetails.length) {
      this.buildingDimensionDetails.pop();
    }

    // Update the form array when the building dimensions change
    this.setFormArray();
  }

  highlightDetailAsModified(index: number, detailName: string, currentDetailValue: any): boolean {
    if (!this.hasActiveDataSet) {
      return false;
    }

    const activeDetail = this.activeDataSetBuildingDimensionDetails.length > index
      ? this.activeDataSetBuildingDimensionDetails[index]
      : null;

    return activeDetail ? activeDetail[detailName] !== currentDetailValue : true;
  }

  partyWallLengthMissing(): boolean {
    if (this.partyWallConstructionNotApplicable) {
      return false;
    }

    return !this.buildingDimensionDetails.some(x => x.partyWall >= 1);
  }

  copyFloor(index: number) {
    // here, check if this is the last row.. do not let user copy if it is 
    if (index === this.buildingDimensionDetails.length - 1) {
      return;
    }
    const itemToCopy = { ...this.buildingDimensionDetails[index] };
  
    // here, if the item to copy has any filled values, let the user copy it, skip copying if it's empty
    if (!itemToCopy.area && !itemToCopy.height && !itemToCopy.partyWall && !itemToCopy.perimeter) {
      return;
    }
  
    // here, check next empty row
    for (let i = index + 1; i < this.buildingDimensionDetails.length; i++) {
      const nextRow = this.buildingDimensionDetails[i];
  
      // here, is the next row empty? COPY data and do not modify floor number
      if (!nextRow.area && !nextRow.height && !nextRow.partyWall && !nextRow.perimeter) {
        this.buildingDimensionDetails[i] = { ...itemToCopy, floor: nextRow.floor };
        break; 
      }
    }
  
    // here, update modal after copying
    this.setFormArray();
  }

  setTitles(index: number) : string {
    if (index === 0) {
      return this.heatedBasement ? 'Heated Basement' : 'Ground';
    } else if (index === 1 && this.heatedBasement) {
      return 'Ground';
    } else {
      return `${index - (this.heatedBasement ? 1 : 0)}${this.getOrdinalSuffix(index - (this.heatedBasement ? 1 : 0))} floor`;
    }
  }

  getOrdinalSuffix(i: number): string {
    const j = i % 10,
          k = i % 100;
    if (j == 1 && k != 11) {
      return "st";
    }
    if (j == 2 && k != 12) {
      return "nd";
    }
    if (j == 3 && k != 13) {
      return "rd";
    }
    return "th";
  }
  
/**
 * 
 * @param start 
 * @param end 
 * @returns Array
 */
  generateRange(start: number, end: number): number[] {
    return Array.from({ length: end - start + 1 }, (_, i) => start + i);
  }

  close() {
    this.activeModal.close({ dimensionDetails: this.buildingDimensionDetails, numberOfFloors: this.numberOfFloors });
  }
}
